import { z } from "zod/v4";

/**
 * `RoadSensePayload` — the decoded contract the RoadSense report renders
 * against. RoadSense is a **traffic-flow / vehicle line-crossing** report: it
 * answers "how many vehicles crossed, in which direction, of what class, and
 * how is that trending?" — distinct from WasteSense's incident/evidence shape.
 *
 * Deliberate differences from `WasteSensePayload`:
 *  - **No priority incidents, no snapshot/evidence images, no per-site event
 *    logs.** RoadSense's centre of gravity is aggregate time-series, not
 *    individual events, so none of the snapshot-url plumbing applies here.
 *  - **The traffic summary is an ordered list, not fixed fields.** The headline
 *    metrics vary per customer (a directional split for one site, entries/peak
 *    for another), so the contract carries an ordered list of pre-formatted
 *    cards rather than a fixed object. This keeps the wire shape stable as
 *    customers add or drop summary cards without a frontend change.
 *  - **A single `site` + a `scenarios` list, not a `map_sites` list.** A
 *    RoadSense render payload is per-site (one payload per site), so the site's
 *    identity (id, title, address, timezone) is a single object — not repeated
 *    on every row as it was under the old `map_sites` shape. `scenarios` then
 *    carries only the per-scenario data (name, asset, coordinates, flow
 *    direction, crossings), which is genuinely distinct per row.
 *
 * Every display string is pre-formatted server-side (same convention as
 * WasteSense) so the renderer never reconstructs labels from raw bounds — the
 * same strings appear in the static export, the email digest, and this screen.
 */

export const RoadSenseReportMeta = z
  .object({
    customer_name: z.string(),
    customer_logo_url: z.string().optional(),
    report_month_year: z.string(),
    report_title: z.string(),
    report_subtitle: z.string(),
    window_label: z.string(),
    window_days: z.number().int().min(1),
  })
  .strict();
export type RoadSenseReportMeta = z.infer<typeof RoadSenseReportMeta>;

/**
 * Direction-of-travel of a summary card's period-over-period change. `flat`
 * covers a genuine no-change as well as the "no prior period to compare" case —
 * the renderer shows a neutral pill in both, so we don't need a separate
 * "unknown".
 */
export const RoadSenseTrafficSummaryDelta = z
  .object({
    direction: z.enum(["up", "down", "flat"]),
    /** Pre-formatted change label, e.g. "+12% vs last week". */
    label: z.string(),
  })
  .strict();
export type RoadSenseTrafficSummaryDelta = z.infer<typeof RoadSenseTrafficSummaryDelta>;

export const RoadSenseTrafficSummaryCard = z
  .object({
    /** Stable per-card key (used for list keys / future client logic). */
    id: z.string(),
    label: z.string(),
    /** Pre-formatted headline value, e.g. "12,480" or "1,204 / day". */
    value: z.string(),
    /** Optional supporting line under the value, e.g. "Southbound". */
    sub_label: z.string().optional(),
    delta: RoadSenseTrafficSummaryDelta.optional(),
  })
  .strict();
export type RoadSenseTrafficSummaryCard = z.infer<typeof RoadSenseTrafficSummaryCard>;

/**
 * The report's single site. A RoadSense render payload is per-site, so the
 * site's identity lives here once rather than being repeated on every scenario
 * row (the old `map_sites` shape duplicated `site_id`/`site_address` across
 * every entry). `site_timezone` is the IANA zone carried for display/labelling.
 */
export const RoadSenseSite = z
  .object({
    site_id: z.string(),
    site_title: z.string(),
    site_address: z.string(),
    site_timezone: z.string(),
  })
  .strict();
export type RoadSenseSite = z.infer<typeof RoadSenseSite>;

/**
 * One counted flow (scenario) at the site. Every scenario appears here —
 * including those an operator has not yet placed on the map — so its name and
 * crossings still surface in a list even without a pin. `map_lat`/`map_lon` and
 * `direction` are therefore optional: absent coordinates mean no map marker,
 * and an absent `direction` means no flow arrow is drawn.
 */
export const RoadSenseScenario = z
  .object({
    /**
     * Stable 1..N pin number, unique **within the site** by construction (see
     * `numberSitePins`). Used as the map marker's list key and matches the pin
     * label drawn on the static map / Summary table, so "pin 3" is the same
     * scenario everywhere. Preferred over `name`/`asset_id` as an identity: a
     * name can repeat and one asset can back several scenarios, so those collide.
     */
    pin_number: z.number().int().min(1),
    /** Scenario display label (operator friendly name, else the raw name). */
    name: z.string(),
    asset_id: z.string(),
    /** GPS coordinate for the map marker. Absent → no marker for this scenario. */
    map_lat: z.number().optional(),
    map_lon: z.number().optional(),
    /**
     * Flow direction as compass degrees clockwise from true north in `[0, 360)`,
     * sourced from the scenario's `bearingDeg`. Drives the map's direction arrow;
     * absent → no arrow drawn.
     */
    direction: z.number().min(0).lt(360).optional(),
    /** Total vehicle crossings observed for this scenario over the window. */
    total_crossings: z.number().int().min(0),
  })
  .strict();
export type RoadSenseScenario = z.infer<typeof RoadSenseScenario>;

/**
 * The traffic time-series the trend chart renders. The edge counts vehicles in
 * **15-minute intervals** (the `TrafficFlowBucket` cadence), so we ship that
 * base resolution and let the chart aggregate up to hour / day / week / … buckets
 * client-side — no backend round trip to re-scope, and the same series feeds the
 * static export and this screen.
 *
 * Series are parallel arrays aligned to `instants` (one entry per interval).
 * Each vehicle-class series is optional so a site that only counts a combined
 * total (no per-class breakdown) can omit them and still render the `total` line.
 */

/**
 * One movement's interval series. A movement is a single counted flow — a travel
 * direction (Northbound) on a simple road, or a discrete counting point
 * (Point 1, Western approach) on a multi-point site. Carries its own combined
 * total plus optional per-class series so the chart can re-scope to a single
 * movement, optionally further narrowed by vehicle class.
 */
export const RoadSenseMovementSeries = z
  .object({
    /** Stable per-movement key (used for chip keys / URL param value). */
    id: z.string(),
    /** Display label for the chip / legend, e.g. "Northbound" or "Point 1". */
    label: z.string(),
    /** Combined count per interval for this movement. */
    total_series: z.array(z.number().int().min(0)),
    /** Per-class interval series for this movement (optional, mirrors top level). */
    car_series: z.array(z.number().int().min(0)).optional(),
    truck_series: z.array(z.number().int().min(0)).optional(),
    bus_series: z.array(z.number().int().min(0)).optional(),
  })
  .strict();
export type RoadSenseMovementSeries = z.infer<typeof RoadSenseMovementSeries>;
export const RoadSenseTrend = z
  .object({
    /** Per-interval axis labels, e.g. "Mon 9 Jun" or "Mon 9 Jun, 11:15". */
    labels: z.array(z.string()),
    /**
     * Interval-start instant per label, as an ISO-8601 datetime **with the
     * camera-local UTC offset** (e.g. `2026-06-09T11:00:00+10:00`). RoadSense
     * reports are read site-locally — labels show the site's wall-clock and the
     * instants carry the matching local time + offset, so a customer sees "11:00"
     * meaning 11:00 at the site, not a UTC-shifted hour. The base cadence is
     * hourly (the 15-minute edge `TrafficFlowBucket`s are summed up to the hour);
     * the client buckets these up for coarser granularities. `offset: true`
     * (rather than UTC-only `Z`) is required because the producer stamps the
     * site-local offset.
     */
    instants: z.array(z.iso.datetime({ offset: true })),
    /** Combined vehicle count per interval — always present. */
    total_series: z.array(z.number().int().min(0)),
    /** Per-class interval series. Optional; present when the site classifies. */
    car_series: z.array(z.number().int().min(0)).optional(),
    truck_series: z.array(z.number().int().min(0)).optional(),
    bus_series: z.array(z.number().int().min(0)).optional(),
    /**
     * Per-**movement** interval series. A "movement" is a single counted flow at
     * a site: a travel direction on a simple road (Northbound/Southbound) **or** a
     * discrete counting point on a multi-point site (Point 1, Western approach,
     * …). We use the neutral "movement" term because the same dimension is a
     * direction in one deployment and a set of points in another.
     *
     * Optional and order-significant (rendered as chips left-to-right). When
     * present, the top-level `*_series` above are the **combined (all-movements)**
     * view; selecting a movement re-scopes the chart to that movement's series.
     * Omitted entirely for single-movement sites, where the combined series is
     * the whole story and no movement chips are shown.
     */
    movements: z.array(RoadSenseMovementSeries).optional(),
  })
  .strict();
export type RoadSenseTrend = z.infer<typeof RoadSenseTrend>;

export const RoadSensePayload = z
  .object({
    report_meta: RoadSenseReportMeta,
    /** Ordered summary cards (varies per customer) — rendered left-to-right. */
    traffic_summary: z.array(RoadSenseTrafficSummaryCard),
    /** The single site this payload renders (identity carried once, not per row). */
    site: RoadSenseSite,
    /** Every counted flow at the site — includes unplaced (no-coordinate) scenarios. */
    scenarios: z.array(RoadSenseScenario),
    trend: RoadSenseTrend,
  })
  .strict();

export type RoadSensePayload = z.infer<typeof RoadSensePayload>;

/**
 * Payload-schema version (design §3.4). **Reset to 1** with the site/scenarios
 * reshape: RoadSense is still pre-release (no customer is served from these
 * payloads — they still receive manually-produced reports), and every existing
 * report is regenerated immediately after this deploys, so there is no stored
 * v2 payload to stay backwards-compatible with. Restarting the count keeps the
 * version honest as "this schema, revision 1" rather than carrying the history
 * of a shape no artefact in the wild uses. Bump from here on breaking changes.
 */
export const ROADSENSE_PAYLOAD_SCHEMA_VERSION = 1;

/**
 * One site as it appears in a RoadSense report's **listing summary** — the
 * frontend reads this to render the site list and to build per-site fetch +
 * download links *without* fetching the manifest. Both keys are the opaque S3
 * keys the artifact-url route presigns: `payloadS3Key` for the render `.json`,
 * `workbookS3Key` for the `.xlsx` download.
 */
export const RoadSenseListingSite = z
  .object({
    siteId: z.string(),
    siteName: z.string(),
    workbookS3Key: z.string(),
    payloadS3Key: z.string(),
  })
  .strict();
export type RoadSenseListingSite = z.infer<typeof RoadSenseListingSite>;

/**
 * Decoder for a RoadSense report row's `listingSummary` (the opaque `z.json()`
 * blob on `ReportListItem`). The generator stores `sites` **JSON-encoded** (the
 * listing-summary values are flat scalars — see the plugin's
 * `summariseForListing`), so this codec parses that inner string back into the
 * typed per-site list. Use it on the frontend after narrowing the row by
 * `type === "roadsense"`; a parse failure means schema drift, not a normal
 * empty state.
 */
export const RoadSenseListingSummary = z
  .object({
    customerName: z.string(),
    siteCount: z.number().int().nonnegative(),
    // Stored as a JSON string by the generator; decode it to the typed array.
    sites: z.codec(z.string(), z.array(RoadSenseListingSite), {
      decode: (json, ctx) => {
        try {
          // Zod re-validates the returned value against `RoadSenseListingSite[]`,
          // so the cast only satisfies the decoder's return type — a malformed
          // shape still fails the parse downstream.
          return JSON.parse(json) as RoadSenseListingSite[];
        } catch {
          // A non-JSON `sites` string is schema drift, not a normal state — fail
          // the parse cleanly so the caller treats it like any decode error
          // rather than crashing on an uncaught SyntaxError.
          ctx.issues.push({
            code: "invalid_format",
            format: "json",
            input: json,
            message: "RoadSense listingSummary.sites is not valid JSON",
          });
          return z.NEVER;
        }
      },
      encode: (sites) => JSON.stringify(sites),
    }),
  })
  .strict();
export type RoadSenseListingSummary = z.infer<typeof RoadSenseListingSummary>;

/**
 * Decodes a report row's opaque `listingSummary` (a `z.json()` field, static
 * type `JSONType`) into a typed {@link RoadSenseListingSummary}.
 *
 * Centralises the one unavoidable cast: `safeDecode`'s parameter is the codec's
 * *encoded* shape, but the row field is `JSONType`. The cast only satisfies the
 * compiler — the codec re-validates the structure (and the inner JSON-encoded
 * `sites` string) at runtime, so a non-matching value still fails the decode.
 * Keeping it here means callers (the frontend hooks) pass the raw field through
 * without each repeating the cast. Narrow the row by `type === "roadsense"`
 * before calling.
 */
export function safeDecodeRoadSenseListing(
  listingSummary: unknown,
): ReturnType<typeof RoadSenseListingSummary.safeDecode> {
  return RoadSenseListingSummary.safeDecode(
    listingSummary as { customerName: string; siteCount: number; sites: string },
  );
}
